import type {MymachineState} from './initial-state'
import type {GetterTree} from 'vuex'

const getters: GetterTree<MymachineState, any> = {
  getCurrentUser: (state) => {
    return state ? state.user : null
  },
  getAppReady: (state) => {
    return state && state.stateRestored && state.user && state.user.email ? true : false
  },
  getCurrentUserKey: (state, getters) => {
    return state && getters.getCurrentUser ? getters.getCurrentUser.email : null
  },
  getIdentity: (state) => {
    return state ? state.identity : null
  },
  getRegionalizedFeatures: (state, getters) => {
    const currentRegion = getters.getRegion
    return state
      ? Object.fromEntries(
        Object.entries(state.features)
          .filter(f => f[0].endsWith(currentRegion))
          .map(f => [f[0].replace(currentRegion, ''), f[1]])
      )
      : {}
  },
  getFeatures: (state) => {
    return state ? state.features : {}
  },
  getRegion: (state, getters) => {
    const _key = getters.getCurrentUserKey
    return _key && state.region[_key] ? state.region[_key] : null
  },
  getLanguage: (state, getters) => {
    const _key = getters.getCurrentUserKey
    const _region = _key && state.region[_key] ? state.region[_key] : null
    return _key && _region && state.language[_key] && state.language[_key][_region]
      ? state.language[_key][_region]
      : null
  },
  getSerials: (state, getters) => {
    const userKey = getters.getCurrentUserKey
    const currentRegion = getters.getRegion

    if (state.serials?.[userKey]?.length && 0 in state.serials?.[userKey] && currentRegion === 'NA') {
      return state.serials[userKey]
    }

    return userKey && currentRegion && state.serials?.[userKey]?.[currentRegion] || null
  },
  getConcepts: (state, getters) => {
    const _region = getters.getRegion
    const _language = getters.getLanguage
    if (state.showConceptNames) return {show: 'concepts'}
    return _region && _language && state.regionConcepts?.[_region]?.[_language]
      || state.regionConcepts?.NA?.en
      || {}
  },
  getAvailableLanguages: (state, getters) => {
    const _region = getters.getRegion
    return _region && state.regionConcepts?.[_region]
      ? Object.keys(state.regionConcepts[_region])
      : null
  },
  getCurrentSerial: (state, getters) => {
    if (!Array.isArray(getters.getSerials)) return null

    const activeSerial = getters.getSerials.find((serial: any) => serial.active === true)
    if (!activeSerial) return null

    return activeSerial.serial_number
  },
  getCurrentMachine: (state, getters) => {
    const _s = getters.getCurrentSerial
    return state.ui.currentMachine[_s] ? state.ui.currentMachine[_s].data : {}
  },
  getCurrentMachineError: (state, getters) => {
    const _s = getters.getCurrentSerial
    return state.ui.currentMachine[_s] ? state.ui.currentMachine[_s].error : null
  },
  getCurrentMachineLoading: (state, getters) => {
    const _s = getters.getCurrentSerial
    return state.ui.currentMachine[_s] ? state.ui.currentMachine[_s].loading : false
  },
  getCurrentAbbreviation: (state, getters) => {
    if (!Array.isArray(getters.getSerials)) return null

    const activeSerial = getters.getSerials.find((serial: any) => serial.active === true)
    if (!activeSerial) return null

    return activeSerial.abbreviation
  },
  getCurrentModel: (state, getters) => {
    const _key = getters.getCurrentUserKey
    return _key && getters.getSerials && getters.getSerials.filter((x: any) => x.active).length
      ? getters.getSerials.filter((x: any) => x.active)[0].model_id
      : null
  },
  getCurrentConnected: (state, getters) => {
    if (getters.getCurrentMachine?.networkType !== '4G' && getters?.getRegion === 'NA') return false

    if (!Array.isArray(getters.getSerials)) return null

    const activeSerial = getters.getSerials.find((serial: any) => serial.active === true)
    if (!activeSerial) return null

    return activeSerial.is_connectable
  },
  isEmpty: (state, getters) => {
    const _key = getters.getCurrentUserKey
    return _key && getters.getSerials && getters.getSerials.length
      ? getters.getSerials.length === 0
      : true
  },
  isCurrentMachineValidSubscription: (state, getters) => {
    const _s = getters.getCurrentSerial
    const _u = getters.getCurrentUser
    if (_u.internal) return true
    else
      return state.ui.currentMachine[_s] &&
        state.ui.currentMachine[_s].assetData &&
        state.ui.currentMachine[_s].assetData.subscriptions &&
        state.ui.currentMachine[_s].assetData.subscriptions.length > 0 &&
        state.ui.currentMachine[_s].assetData.subscriptions.find(
          (subs: any) => new Date() < new Date(subs.expiration)
        ) || false
  },
  isCurrentMachineAuthorized: (state, getters) => (context: string) => {
    if (context === 'dealer') return true
    const _s = getters.getCurrentSerial
    return state.ui.authorisation[_s] && state.ui.authorisation[_s].machineAuthorized || false
  },
  getFaultCodesKeyword: (state) => {
    return state.faultcodes.keyword
  },
  getTroubleshootingKeyword: (state) => {
    return state.ui.troubleshooting.keyword
  },
  getTroubleshootingSearchType: (state) => {
    return state.ui.troubleshooting.searchType
  },
  getSelectedOrganization: (state) => {
    return state.organizations
  },
  getFilteredOrganization: (state) => {
    return state.filteredOrganization
  },
  getSNErrorStatus: (state) => {
    return state.ui.wrongSN
  },
  isSetRegion: (state, getters) => {
    return !!getters.getRegion
  },
  getMachinePassword: (state) => {
    return state.machinePassword
  },
  getFilters: (state, getters) => {
    const userKey = getters.getCurrentUserKey
    return state.filters && state.filters[userKey]
  },
  getMachineImage: (state, getters) => {
    if (!Array.isArray(getters.getSerials)) return null

    const activeSerial = getters.getSerials.find((serial: any) => serial.active === true)
    if (!activeSerial && !activeSerial.image) return null

    return activeSerial.image
  }
}

export default getters
